/**
 * The manual's chrome: a translucent header, the chapter list, and the pager.
 *
 * The list is drawn in three shapes, and they are not duplicates of each
 * other. On the contents page it is the page — the search over it, then two
 * columns of numbered titles. Beside a chapter it is a sticky rail: the same
 * order, smaller, with the chapter you are reading on a soft pill that springs
 * from one chapter to the next.
 *
 * On a window too narrow for the rail the chapter is the whole column, the
 * pager under it is the navigation, and the header's Contents button opens the
 * list as a sheet (`ContentsSheet.tsx`) — the only shape that leaves room for a
 * screenshot.
 */
import { useEffect, useRef, useState } from 'react';
import type React from 'react';
import { AirlockMark } from './AirlockMark';
import { SHEET_ID } from './ContentsSheet';
import { Link, pad } from './DocsLink';
import { ACCOUNT_URL, SITE_URL } from '../lib/links';
import { partsOf, type Doc } from '../lib/content';
import { useRailHighlight } from '../lib/motion';
import { CONTENTS } from '../lib/router';

/**
 * AIRLOCK | MANUAL, then Site and an outlined Account pill. Under the rail's
 * breakpoint a Contents button stands before Account and opens the sheet; Site
 * goes at phone width, the site being one tap away through the mark.
 */
export function ManualHeader({
  onGo,
  sheetOpen,
  onSheet
}: {
  onGo: (id: string) => void;
  sheetOpen: boolean;
  onSheet: () => void;
}): React.JSX.Element {
  return (
    <header className="al-header">
      <div className="al-header-in">
        <a className="al-brand al-press" href={SITE_URL}>
          <AirlockMark size={24} />
          <span className="al-brand-word">AIRLOCK</span>
        </a>
        <span className="al-header-rule" aria-hidden="true" />
        <Link id={CONTENTS} onGo={onGo} className="al-header-manual">
          MANUAL
        </Link>
        <span style={{ flex: 1 }} />
        <a className="al-header-site al-press" href={SITE_URL}>
          Site
        </a>
        <button
          type="button"
          className="al-header-contents al-press"
          aria-expanded={sheetOpen}
          aria-controls={SHEET_ID}
          onClick={onSheet}
        >
          Contents
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M7 10l5 5 5-5" />
          </svg>
        </button>
        <a className="al-header-account al-press" href={ACCOUNT_URL}>
          Account
        </a>
      </div>
    </header>
  );
}

/**
 * The sticky header's height, and the air under it, at the rail's breakpoint —
 * none: the rail's own 36px of padding is the air.
 */
const HEADER = 56;
const RAIL_GAP = 0;
/** And how much air is left under the rail when it does not fit. */
const RAIL_FOOT = 24;

/**
 * The sticky top the chapter rail should hold at, in pixels.
 *
 * `min(header, viewportHeight − railHeight − 24)`. A window with room for
 * every chapter gets 56 and the rail stays under the header, motionless. A
 * shorter one gets a negative number, which is what makes a sticky element
 * travel up with the page and stop once its own bottom reaches the fold — the
 * list scrolls itself into view using the page's scroll, and needs no scroller
 * of its own.
 *
 * Measured rather than guessed because the rail's height is the chapter count,
 * and the chapter count is a directory listing.
 */
export function useRailTop(rail: React.RefObject<HTMLElement | null>): number {
  const [top, setTop] = useState(HEADER + RAIL_GAP);

  useEffect(() => {
    const element = rail.current;
    if (!element) return;

    const measure = (): void => {
      const height = element.getBoundingClientRect().height;
      setTop(Math.min(HEADER + RAIL_GAP, window.innerHeight - height - RAIL_FOOT));
    };

    measure();
    window.addEventListener('resize', measure);
    // The rail's height changes without the window's when the fonts arrive.
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => {
      window.removeEventListener('resize', measure);
      observer.disconnect();
    };
  }, [rail]);

  return top;
}

/**
 * The rail beside a chapter: every chapter, in order and under its part, with
 * this one on the springing pill (`useRailHighlight`).
 *
 * All of them, always — the list has no scroller of its own, and the sidebar
 * around it sticks by its bottom edge so reading down the page brings the end
 * of the list into view. CONTENTS at its head is the way to the search.
 */
export function ChapterRail({
  docs,
  current,
  onGo
}: {
  docs: Doc[];
  current: string;
  onGo: (id: string) => void;
}): React.JSX.Element {
  const rail = useRef<HTMLElement>(null);
  const pill = useRef<HTMLSpanElement>(null);
  useRailHighlight(rail, pill, current);

  return (
    <nav className="al-rail" aria-label="Chapters" ref={rail}>
      <span className="al-rail-hl" ref={pill} aria-hidden="true" />
      <Link id={CONTENTS} onGo={onGo} className="al-label al-rail-head">
        Contents
      </Link>
      {partsOf(docs).map((part) => (
        <div key={part.title} className="al-rail-part">
          <div className="al-label al-rail-part-title">{part.title}</div>
          <ol className="al-rail-list">
            {part.docs.map((doc) => (
              <li key={doc.id}>
                <Link id={doc.id} onGo={onGo} current={doc.id === current} className="al-rail-link">
                  <span className="al-rail-n">{pad(doc.order)}</span>
                  <span className="al-rail-title">
                    {doc.title}
                    {doc.beta && doc.id !== current ? <span className="al-beta">BETA</span> : null}
                  </span>
                </Link>
              </li>
            ))}
          </ol>
        </div>
      ))}
    </nav>
  );
}

/** The tag beside a beta chapter's title. The word carries the state, not a colour. */
export function BetaTag(): React.JSX.Element {
  return <span className="al-beta">BETA</span>;
}

/**
 * The table of contents: the chapters in the order they are meant to be read,
 * under the part each belongs to, each carrying the number its own frontmatter gives it. Printed rather than
 * counted, because the search filters this list — and a chapter's number has to
 * stay the chapter's number when the eight around it are hidden.
 *
 * « / » puts the cursor in the search, Esc empties it.
 */
export function Contents({
  docs,
  total,
  query,
  onQuery,
  onGo
}: {
  docs: Doc[];
  total: number;
  query: string;
  onQuery: (value: string) => void;
  onGo: (id: string) => void;
}): React.JSX.Element {
  const input = useRef<HTMLInputElement>(null);
  const searching = query.trim() !== '';

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))) return;
      event.preventDefault();
      input.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const clear = (): void => {
    onQuery('');
    input.current?.focus();
  };

  return (
    <div>
      <div className="al-label al-eyebrow">{total} chapters</div>
      <h1 className="al-h1">
        The Airlock manual
      </h1>
      <p className="al-contents-lede">
        From the download to the FAQ, one chapter after the other. Read it in order the first time; after that, every
        chapter stands on its own.
      </p>

      <div className="al-search-row">
        <div className="al-search">
          <input
            ref={input}
            type="search"
            value={query}
            onChange={(event) => onQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape' && query) {
                event.preventDefault();
                onQuery('');
              }
            }}
            placeholder="Search the chapters"
            aria-label="Search the chapters"
          />
          {query ? (
            <button type="button" className="al-search-clear al-press" title="Clear (Esc)" aria-label="Clear the search" onClick={clear}>
              ×
            </button>
          ) : (
            <kbd className="al-key" aria-hidden="true">
              /
            </kbd>
          )}
        </div>
        <span className="al-search-count" aria-live="polite">
          {searching ? `${docs.length} of ${total}` : `${total} chapters`}
        </span>
      </div>

      {docs.length === 0 ? (
        <div className="al-empty">
          <div className="al-empty-title">No chapter matches “{query.trim()}”.</div>
          <div className="al-empty-hint">The search reads chapter titles and their one-line summaries.</div>
          <button type="button" className="al-btn al-press" onClick={clear}>
            Clear
          </button>
        </div>
      ) : (
        <nav aria-label="Chapters" className="al-toc-parts">
          {partsOf(docs).map((part) => (
            <section key={part.title} className="al-toc-part">
              <h2 className="al-label al-toc-part-title">{part.title}</h2>
              <ol className="al-toc">
                {part.docs.map((doc) => (
                  <li key={doc.id}>
                    <Link id={doc.id} onGo={onGo}>
                      <span className="al-toc-n">{pad(doc.order)}</span>
                      <span className="al-toc-title">{doc.title}</span>
                      {doc.beta ? <BetaTag /> : null}
                    </Link>
                  </li>
                ))}
              </ol>
            </section>
          ))}
        </nav>
      )}
    </div>
  );
}

/**
 * ← previous · contents · next → under every chapter, and with the header's
 * sheet the navigation a chapter carries under 1024. At phone width the two
 * neighbours are two cells of at least 44 and Contents a row of its own under
 * them.
 */
export function Pager({
  previous,
  next,
  onGo
}: {
  previous?: Doc;
  next?: Doc;
  onGo: (id: string) => void;
}): React.JSX.Element {
  return (
    <nav aria-label="Chapter" className="al-pager">
      <div className="al-pager-cells">
        {previous ? (
          <Link id={previous.id} onGo={onGo} className="al-pager-side al-pager-prev al-press">
            <span className="al-label">
              ← {pad(previous.order)}
              <span className="al-pager-long"> · Previous</span>
            </span>
            <span className="al-pager-title">{previous.title}</span>
          </Link>
        ) : (
          <span className="al-pager-side al-pager-prev" />
        )}
        {next ? (
          <Link id={next.id} onGo={onGo} className="al-pager-side al-pager-next al-press">
            <span className="al-label">
              <span className="al-pager-long">Next · </span>
              {pad(next.order)} →
            </span>
            <span className="al-pager-title">{next.title}</span>
          </Link>
        ) : (
          <span className="al-pager-side al-pager-next" />
        )}
      </div>
      <Link id={CONTENTS} onGo={onGo} className="al-pager-contents al-press">
        Contents
      </Link>
    </nav>
  );
}
